import { useRef, useState, type ReactNode } from 'react';

interface SwipeToDeleteProps {
  onDelete: () => void;
  children: ReactNode;
  deleteWidth?: number;
  disabled?: boolean;
}

const SwipeToDelete = ({ onDelete, children, deleteWidth = 80, disabled = false }: SwipeToDeleteProps) => {
  const [offsetX, setOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled) return;
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = offsetX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const diff = e.touches[0].clientX - startXRef.current + currentXRef.current;
    // 只允许向左滑，最多滑 deleteWidth + 10px
    const clamped = Math.min(0, Math.max(-(deleteWidth + 10), diff));
    setOffsetX(clamped);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    // 超过一半就吸附到完全展开，否则回弹
    if (offsetX < -(deleteWidth / 2)) {
      setOffsetX(-deleteWidth);
    } else {
      setOffsetX(0);
    }
  };

  const handleDelete = () => {
    setIsDeleting(true);
    setTimeout(() => {
      onDelete();
    }, 350);
  };

  const handleContentClick = () => {
    if (offsetX !== 0) {
      setOffsetX(0);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-xl"
      style={{
        opacity: isDeleting ? 0 : 1,
        maxHeight: isDeleting ? 0 : '200px',
        marginBottom: isDeleting ? 0 : undefined,
        transition: isDeleting
          ? 'opacity 0.25s ease, max-height 0.35s ease, margin-bottom 0.35s ease'
          : 'none',
      }}
    >
      {/* 红色删除背景 */}
      <div
        className="absolute right-0 top-0 bottom-0 bg-rose-500 flex items-center justify-center rounded-xl"
        style={{ width: deleteWidth }}
      >
        <button
          onClick={handleDelete}
          className="flex flex-col items-center justify-center w-full h-full cursor-pointer"
        >
          <i className="ri-delete-bin-line text-white text-xl" />
          <span className="text-white text-xs mt-1">删除</span>
        </button>
      </div>

      {/* 内容区域 */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleContentClick}
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeToDelete;
